import type { CachedCreativeSettings, CreativeType } from '../models/creativeInsight.types'

import {
  SUMMARY_CREATIVE_DIMENSIONS,
  SUMMARY_CREATIVE_METRICS,
  type ScheduleTab,
  type WorkspaceSchedule,
} from '@/features/reports'

export interface CreativeFieldSelection {
  available: string[]
  dimensions: string[]
  metrics: string[]
  tabs: string[]
}

function scheduleTab(value: unknown): value is ScheduleTab {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function creativeCacheId(type: CreativeType): string {
  return `dash_creative_slider${type}`
}

export function discoverCreativeTypes(schedule?: WorkspaceSchedule | null): CreativeType[] {
  const types = new Set<CreativeType>()
  for (const name of Object.keys(schedule?.additional_info?.tabs ?? {})) {
    const normalized = name.toLowerCase()
    if (normalized.includes('image')) types.add('Image')
    if (normalized.includes('video')) types.add('Video')
  }
  return [...types]
}

export function resolveCreativeFields(
  type: CreativeType,
  schedule?: WorkspaceSchedule | null,
  saved?: CachedCreativeSettings,
): CreativeFieldSelection {
  const available = new Set<string>()
  const tabs: string[] = []
  for (const [name, value] of Object.entries(schedule?.additional_info?.tabs ?? {})) {
    if (!scheduleTab(value) || value.partition_name !== 'metrics_ad') continue
    tabs.push(name)
    for (const field of value.columns ?? []) available.add(field)
    for (const field of value.metrics ?? []) available.add(field)
  }

  const fields = [...available]
  const dimensions = selectedFields(saved?.dimensions, SUMMARY_CREATIVE_DIMENSIONS, fields)
  const metrics = selectedFields(saved?.metrics, SUMMARY_CREATIVE_METRICS, fields)
  const mediaField = type === 'Image' ? 'image_url' : 'thumbnail_url'
  if (!dimensions.includes(mediaField)) dimensions.unshift(mediaField)
  return { available: fields, dimensions, metrics, tabs }
}

function selectedFields(
  saved: CachedCreativeSettings['dimensions'] | CachedCreativeSettings['metrics'],
  fallback: readonly string[],
  available: string[],
): string[] {
  const fields = Array.isArray(saved) ? saved.map((column) => column.field) : fallback
  return fields.filter((field) => available.includes(field))
}
